import { getAuth } from 'firebase/auth';
import { app } from '@/config/firebaseConfig';
import { generateUUID } from './someUtils';

export type KazeFetchRequestInit = RequestInit & {
  isStream?: boolean;
  isReturnResponse?: boolean;
};

/**
 * @description 请求接口
 * @param url 请求地址
 * @param options 请求选项
 * @param options.isStream 是否返回流
 * @returns 请求结果
 */
export const kazeFetch = async (url: string, options: KazeFetchRequestInit) => {
  const auth = getAuth(app);
  await auth.authStateReady();
  const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

  // 获取或生成设备ID
  let deviceId = localStorage.getItem('kaze-device-id');
  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem('kaze-device-id', deviceId);
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      ...(options.body instanceof FormData
        ? {}
        : { 'Content-Type': 'application/json' }),
      'X-Device-Id': deviceId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  if (options.isStream) return res.body;
  if (options.isReturnResponse) return res;

  const data = await res.json();
  if (res.ok) return data;
  else throw data.error;
};

// 用于存储节流函数的上次执行时间
const throttleTimers: Record<string, number> = {};

/**
 * @description 节流版请求接口，N秒内只执行第一次请求
 * @param url 请求地址
 * @param options 请求选项
 * @param delay 节流时间（毫秒），默认1000ms
 * @returns 请求结果
 */
export const throttleKazeFetch = async (
  url: string,
  options: KazeFetchRequestInit,
  delay: number = 1000
) => {
  // 生成请求的唯一标识
  const key = `${url}-${options.method || 'GET'}-${JSON.stringify(options.body || {})}`;
  const now = Date.now();

  // 如果是首次调用或已超过节流时间，则执行请求
  if (!throttleTimers[key] || now - throttleTimers[key] >= delay) {
    throttleTimers[key] = now;
    return kazeFetch(url, options);
  }

  return Promise.resolve('wait');
};

// 用于存储防抖函数的定时器
const debounceTimers: Record<string, NodeJS.Timeout> = {};

/**
 * @description 防抖版请求接口，N秒内只执行最后一次请求
 * @param url 请求地址
 * @param options 请求选项
 * @param delay 防抖时间（毫秒），默认300ms
 * @returns 请求结果的Promise
 */
export const debounceKazeFetch = (
  url: string,
  options: KazeFetchRequestInit,
  delay: number = 300
) => {
  return new Promise((resolve, reject) => {
    // 生成请求的唯一标识
    const key = `${url}-${options.method || 'GET'}-${JSON.stringify(options.body || {})}`;

    // 清除之前的定时器
    if (debounceTimers[key]) {
      clearTimeout(debounceTimers[key]);
    }

    // 设置新的定时器
    debounceTimers[key] = setTimeout(async () => {
      try {
        const result = await kazeFetch(url, options);
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }, delay);
  });
};
